import {
  GROCERY_CATEGORIES,
  type PantryItem,
  type PantryItemWriteInput,
  type PantryMatch,
} from '@recipes/contracts';
import { formatQuantityAmount } from '@recipes/domain';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  EmptyState,
  Input,
  Label,
  Slider,
  Spinner,
} from '@recipes/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { differenceInCalendarDays, parseISO } from 'date-fns';
import {
  AlertTriangle,
  ChefHat,
  MoreHorizontal,
  Pencil,
  Plus,
  ScanBarcode,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Illustration } from '@/features/appearance/illustration';
import { useFeature } from '@/features/shared/hooks';
import { Link, useSearchParams } from 'react-router';
import { PantryItemDialog } from '@/features/pantry/pantry-item-dialog';
import { ConfirmDialog } from '@/features/shared/confirm-dialog';
import { HouseholdSelect } from '@/features/shared/household-select';
import { formatDate, groceryCategoryLabel } from '@/features/shared/labels';
import { FeaturePageHeader, PageContainer } from '@/features/shared/page-header';
import { CompactRecipeCard } from '@/features/shared/recipe-card';
import { api } from '@/lib/api';
import { describeError, toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

function expiryState(expiresAt: string | null): 'expired' | 'soon' | null {
  if (!expiresAt) return null;
  const days = differenceInCalendarDays(parseISO(expiresAt), new Date());
  if (days < 0) return 'expired';
  if (days <= 3) return 'soon';
  return null;
}

export function PantryPage() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [params, setParams] = useSearchParams();
  const householdId = params.get('household');
  const catalogEnabled = useFeature('ingredient_catalog');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState<PantryItem | null>(null);
  const [deleteItem, setDeleteItem] = useState<PantryItem | null>(null);

  const query = useQuery({
    queryKey: [...queryKeys.pantry.all, householdId ?? null],
    queryFn: () => api.pantry.list(householdId ? { householdId } : {}),
  });
  const items = useMemo(() => query.data ?? [], [query.data]);
  const groups = useMemo(() => {
    const map = new Map<string, PantryItem[]>();
    for (const item of items)
      map.set(item.groceryCategory, [...(map.get(item.groceryCategory) ?? []), item]);
    return GROCERY_CATEGORIES.filter((c) => map.has(c)).map((c) => ({
      category: c,
      items: (map.get(c) as PantryItem[]).sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [items]);
  const invalidate = () => void queryClient.invalidateQueries({ queryKey: queryKeys.pantry.all });

  const save = useMutation({
    mutationFn: (input: Omit<PantryItemWriteInput, 'householdId'>) =>
      editItem
        ? api.pantry.update(editItem.id, input)
        : api.pantry.create({ ...input, householdId }),
    onSuccess: () => {
      setDialogOpen(false);
      setEditItem(null);
      invalidate();
    },
    onError: (e) => toastError(e),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.pantry.remove(id),
    onSuccess: () => {
      setDeleteItem(null);
      invalidate();
    },
    onError: (e) => toastError(e),
  });

  return (
    <PageContainer>
      <FeaturePageHeader
        title={t('pantry.title')}
        description={t('pantry.description')}
        actions={
          <>
            {catalogEnabled && (
              <Button asChild variant="outline">
                <Link to="/catalog/scan?target=pantry" data-testid="pantry-scan">
                  <ScanBarcode aria-hidden /> {t('catalog.scan.action')}
                </Link>
              </Button>
            )}
            <Button
              onClick={() => {
                setEditItem(null);
                setDialogOpen(true);
              }}
              data-testid="pantry-add"
            >
              <Plus aria-hidden /> {t('pantry.add')}
            </Button>
          </>
        }
      >
        <HouseholdSelect
          className="mt-3 max-w-xs"
          value={householdId}
          onChange={(v) =>
            setParams((p) => {
              if (v) p.set('household', v);
              else p.delete('household');
              return p;
            })
          }
        />
      </FeaturePageHeader>

      <div className="grid gap-6 lg:grid-cols-[1fr_minmax(20rem,26rem)]">
        <div>
          {query.isLoading && <Spinner label={t('app.loading')} />}
          {query.error && <Alert variant="destructive">{describeError(query.error)}</Alert>}
          {!query.isLoading && items.length === 0 && (
            <EmptyState
              illustration={<Illustration name="pantry" />}
              title={t('pantry.emptyTitle')}
              description={t('pantry.emptyBody')}
            />
          )}
          {groups.map((group) => (
            <section
              key={group.category}
              aria-labelledby={`pantry-${group.category}`}
              className="mb-4"
            >
              <h2
                id={`pantry-${group.category}`}
                className="mb-1 text-sm font-semibold uppercase tracking-wide text-muted-foreground"
              >
                {groceryCategoryLabel(t, group.category)}
              </h2>
              <ul className="divide-y rounded-lg border">
                {group.items.map((item) => {
                  const state = expiryState(item.expiresAt);
                  const amount = formatQuantityAmount(
                    {
                      quantity: item.quantity,
                      quantityMax: null,
                      unitCode: item.unitCode,
                      unitText: item.unitText,
                    },
                    currentLocale(),
                  );
                  return (
                    <li
                      key={item.id}
                      className="flex min-h-[48px] items-center gap-3 px-3 py-1"
                      data-testid="pantry-item"
                    >
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-medium">
                          {item.name}
                          {amount && (
                            <span className="ml-2 font-normal text-muted-foreground">{amount}</span>
                          )}
                        </span>
                        <span className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                          {item.expiresAt && (
                            <span>
                              {t('pantry.expires')}: {formatDate(item.expiresAt, i18n.language)}
                            </span>
                          )}
                          {state === 'expired' && (
                            <Badge variant="destructive">
                              <AlertTriangle className="mr-1 size-3" aria-hidden />{' '}
                              {t('pantry.expired')}
                            </Badge>
                          )}
                          {state === 'soon' && (
                            <Badge variant="warning">
                              <AlertTriangle className="mr-1 size-3" aria-hidden />{' '}
                              {t('pantry.expiresSoon')}
                            </Badge>
                          )}
                          {item.alwaysInStock && (
                            <Badge variant="info" data-testid="pantry-always-in-stock-badge">
                              {t('pantry.alwaysInStock')}
                            </Badge>
                          )}
                          {item.note && <span>{item.note}</span>}
                        </span>
                      </span>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            aria-label={t('pantry.actions', { name: item.name })}
                          >
                            <MoreHorizontal aria-hidden />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setEditItem(item);
                              setDialogOpen(true);
                            }}
                          >
                            <Pencil aria-hidden /> {t('common.edit')}
                          </DropdownMenuItem>
                          <DropdownMenuItem destructive onSelect={() => setDeleteItem(item)}>
                            <Trash2 aria-hidden /> {t('common.delete')}
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}
        </div>
        <CookWithPanel householdId={householdId} />
      </div>

      <PantryItemDialog
        open={dialogOpen}
        onOpenChange={(o) => {
          setDialogOpen(o);
          if (!o) setEditItem(null);
        }}
        item={editItem}
        saving={save.isPending}
        onSubmit={(input) => save.mutate(input)}
      />
      <ConfirmDialog
        open={Boolean(deleteItem)}
        onOpenChange={(o) => !o && setDeleteItem(null)}
        title={t('pantry.deleteTitle')}
        description={deleteItem?.name}
        destructive
        confirmLabel={t('common.delete')}
        loading={remove.isPending}
        onConfirm={() => {
          if (deleteItem) remove.mutate(deleteItem.id);
        }}
      />
    </PageContainer>
  );
}

function CookWithPanel({ householdId }: { householdId: string | null }) {
  const { t } = useTranslation();
  const [extra, setExtra] = useState<string[]>([]);
  const [draft, setDraft] = useState('');
  const [maxMissing, setMaxMissing] = useState(3);
  const match = useMutation({
    mutationFn: () =>
      api.pantry.match({ extraIngredients: extra, householdId, maxMissing, limit: 20 }),
  });
  const addChip = () => {
    const v = draft.trim();
    if (v && !extra.includes(v)) setExtra((prev) => [...prev, v].slice(0, 50));
    setDraft('');
  };

  return (
    <Card className="h-fit">
      <CardContent className="space-y-4 p-4">
        <h2 className="flex items-center gap-2 text-lg font-semibold">
          <ChefHat aria-hidden /> {t('pantry.match.title')}
        </h2>
        <p className="text-sm text-muted-foreground">{t('pantry.match.description')}</p>
        <div>
          <Label htmlFor="extra-ingredient">{t('pantry.match.extra')}</Label>
          <div className="mt-1 flex gap-2">
            <Input
              id="extra-ingredient"
              value={draft}
              maxLength={80}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addChip();
                }
              }}
            />
            <Button
              type="button"
              variant="outline"
              onClick={addChip}
              disabled={!draft.trim()}
              aria-label={t('pantry.match.addExtra')}
            >
              <Plus aria-hidden />
            </Button>
          </div>
          {extra.length > 0 && (
            <ul className="mt-2 flex flex-wrap gap-2" aria-label={t('pantry.match.extra')}>
              {extra.map((e) => (
                <li key={e}>
                  <span className="inline-flex items-center gap-1 rounded-full border px-2 py-1 text-xs">
                    {e}
                    <button
                      type="button"
                      className="rounded p-0.5 hover:bg-accent"
                      aria-label={t('pantry.match.removeExtra', { name: e })}
                      onClick={() => setExtra((prev) => prev.filter((x) => x !== e))}
                    >
                      <X className="size-3" aria-hidden />
                    </button>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div>
          <Label id="max-missing-label">
            {t('pantry.match.maxMissing', { count: maxMissing })}
          </Label>
          <Slider
            aria-labelledby="max-missing-label"
            className="mt-2"
            min={0}
            max={10}
            step={1}
            value={[maxMissing]}
            onValueChange={([v]) => setMaxMissing(v ?? 3)}
          />
        </div>
        <Button type="button" loading={match.isPending} onClick={() => match.mutate()}>
          {t('pantry.match.search')}
        </Button>
        {match.error && <Alert variant="destructive">{describeError(match.error)}</Alert>}
        {match.data && match.data.matches.length === 0 && (
          <p className="text-sm text-muted-foreground">{t('pantry.match.none')}</p>
        )}
        {match.data && match.data.matches.length > 0 && (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
            {match.data.matches.map((m) => (
              <li key={m.recipeId}>
                <MatchCard match={m} />
              </li>
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
}

function MatchCard({ match }: { match: PantryMatch }) {
  const { t } = useTranslation();
  return (
    <CompactRecipeCard
      recipe={{
        id: match.recipeId,
        title: match.title,
        coverThumbnailUrl: match.coverThumbnailUrl,
        totalTimeMinutes: match.totalTimeMinutes,
      }}
      footer={
        <div className="space-y-1">
          <p>
            <Badge variant="success">
              {t('pantry.match.matched', { count: match.matchedIngredients.length })}
            </Badge>{' '}
            <Badge variant={match.missingIngredients.length ? 'warning' : 'muted'}>
              {t('pantry.match.missing', { count: match.missingIngredients.length })}
            </Badge>
          </p>
          {match.missingIngredients.length > 0 && (
            <p className="text-muted-foreground">
              {t('pantry.match.missingList')}: {match.missingIngredients.join(', ')}
            </p>
          )}
          <p className="text-muted-foreground">{t('pantry.match.notExact')}</p>
        </div>
      }
    />
  );
}
